import axios, {CancelToken} from '@/plugin/axios'
import api from '@/api'
import qs from 'qs'

const MAX_LIMIT = 100
const cancelFn: Record<string, (reason?: string) => void> = {}

export const machine = {
  fetchFaultCodes,
  fetchFaultCodesAutocomplete,
  fetchMachinePassword,
  updateMachinePassword,
  fetchMachineData,
  fetchMachineHistory,
  fetchControllers,
  fetchWarrantyInclusions
}

function fetchFaultCodes(
  _sn: string,
  _limit: number,
  _offset: number,
  _params: Record<string, any>,
  _sorting: string = '+faultStatus',
  _paginationLimit?: number,
  cancelFnName: string = 'faultCodes'
): Promise<any> {
  _paginationLimit = _paginationLimit || MAX_LIMIT

  if (_limit > _paginationLimit) {
    return new Promise((resolve, reject) => {
      let allCodes: any[] = []
      ;(async function () {
        let finished = false
        let offset = _offset
        let remained: number | null = null

        while (!finished) {
          remained = Math.min(_limit - allCodes.length, _paginationLimit!)
          if (remained && remained > 0) {
            const r = await apiCall(_sn, remained, offset, _params, _sorting, cancelFnName)
            if (r.items && r.items.length) {
              allCodes = allCodes.concat(r.items)
              offset += r.items.length
              if (r.items.length < remained) finished = true
            } else finished = true
          } else finished = true
        }
        resolve({items: allCodes})
      })().catch(e => reject(e))
    })
  } else {
    return apiCall(_sn, _limit, _offset, _params, _sorting, cancelFnName)
  }
}

function apiCall(
  _sn: string,
  _limit: number,
  _offset: number,
  _params: Record<string, any>,
  _sorting: string = '+faultStatus',
  cancelFnName: string
): Promise<any> {
  if (typeof cancelFn[cancelFnName] === 'function') cancelFn[cancelFnName]('isCancelled')
  return new Promise((resolve, reject) => {
    const combinedParams = {
      sort_key: _sorting,
      offset: _offset,
      ..._params,
      limit: _limit
    }

    const url = _params?.isPDF ? api.endpoints.DPMM_API.FAULT_CODES_LIST : api.endpoints.DPMM_API.AGGREGATED_FAULT_CODES_LIST
    axios.get(url.replace('{serial}', _sn.toLowerCase()), {
      params: combinedParams,
      paramsSerializer: (params: Record<string, any>) => qs.stringify(params, {arrayFormat: 'repeat'}),
      cancelToken: new CancelToken(function executor(c) { cancelFn[cancelFnName] = c })
    }).then(r => {
      resolve(r.data)
    }).catch(e => {
      reject(e)
    })
  })
}

function fetchFaultCodesAutocomplete(_codePrefix: string): Promise<any> {
  return axios.get(api.endpoints.DPMM_API.FAULT_CODES_AUTOCOMPLETE, {
    params: {
      code_prefix: _codePrefix
    }
  })
}

function fetchMachinePassword(serialNumber: string): Promise<any> {
  return axios.get(api.getEndpoint(api.endpoints.DPMM_API.MACHINE_PASSWORD, {
    serial: serialNumber.toUpperCase()
  }))
}

function updateMachinePassword(serialNumber: string, panel: string): Promise<any> {
  return axios.put(api.getEndpoint(api.endpoints.DPMM_API.MACHINE_PASSWORD_UPDATE, {
    serial: serialNumber.toUpperCase(),
    panel
  }))
}

function fetchMachineData(_sn: string): Promise<any> {
  return axios.get(api.endpoints.DPMM_API.MACHINE_DATA.replace('{serial}', _sn))
}

function fetchMachineHistory(serialNumber: string, region: string): Promise<any> {
  return axios.get(api.getEndpoint(api.endpoints.DPMM_API.MACHINE_HISTORY, {
    serial: serialNumber.toUpperCase()
  }), {
    params: {business_unit: region.toUpperCase()}
  })
}

function fetchControllers(serial: string): Promise<any> {
  const endpoint = api.getEndpoint(api.endpoints.DPMM_API.CONTROLLERS_DATA, {serial})
  return axios.get(endpoint)
}

function fetchWarrantyInclusions(serial: string, plan_code: string): Promise<any> {
  const endpoint = api.getEndpoint(api.endpoints.DPMM_API.WARRANTY_INCLUSIONS, {serial})
  return axios.get(endpoint, {params: {plan_code}})
}
